<template>
  <VContainer>
    <AppPreloader v-if="vacancy.isFetching || company.isFetching" />
    <VAlert v-else-if="vacancy.fetchError || company.fetchError" type="error">
      {{ vacancy.fetchError || company.fetchError }}
    </VAlert>
    <div v-else-if="vacancy.data && company.data">
      <slot
        :company="company.data"
        :vacancy="vacancy.data"
        :is-locked="isLocked"
        :is-reply-available="isReplyAvailable"
        :is-vacancy-compiling="isVacancyCompiling"
      />
    </div>
    <div v-else />
  </VContainer>
</template>

<script>
import { mapState } from 'vuex';
import AppPreloader from '../AppPreloader.vue';
import { safeGet } from '../../functions';
import { isLocked } from './functions';

export default {
  name: 'VacancyContainer',
  components: { AppPreloader },
  props: {
    id: {
      type: Number,
      required: true,
    },
    companyId: {
      type: Number,
      required: false,
      default: null,
    },
  },
  computed: {
    ...mapState('company', ['company']),
    ...mapState('vacancy', ['vacancy']),
    isLocked() {
      return isLocked(this.vacancy.data);
    },
    isReplyAvailable() {
      return safeGet(this.vacancy, ['data', 'permissions', 'storeCandidates'], false);
    },
    isVacancyCompiling() {
      return this.vacancy?.data?.aiQuestionsGenerating
        || (this.vacancy?.data?.aiQuestionsGenerated && this.vacancy?.data?.isAiInterviewCompiling);
    },
  },
  async serverPrefetch() {
    await this.fetchServer();
  },
  watch: {
    // eslint-disable-next-line func-names
    '$route.params.vacancyId': async function () {
      await this.fetchClient();
    },
  },
  async mounted() {
    await this.fetchClient();
    if (this.vacancy.fetchError && [403, 404].includes(this.vacancy.fetchCode)) {
      // https://github.com/vuejs/vue-router/issues/977
      const { path } = this.$router.currentRoute;
      await this.$router.replace({ name: '404' });
      window.history.replaceState({}, 'Not Found', path);
    }
  },
  methods: {
    async fetchServer() {
      await Promise.all([
        this.fetchVacancy(this.id),
        this.fetchCompany(this.$route.params.companyId || this.companyId),
      ]);

      if (this.company.fetchError) {
        throw new Error(String(this.company.fetchCode));
      }
      if (this.vacancy.fetchError) {
        throw new Error(String(this.vacancy.fetchCode === 403 ? 404 : this.vacancy.fetchCode));
      }
      this.$emit('ready', { vacancy: this.vacancy.data });
    },
    async fetchClient() {
      const actions = [];
      if (Number(safeGet(this.company, ['data', 'id'])) !== Number(this.$route.params.companyId)) {
        actions.push(this.fetchCompany(this.$route.params.companyId || this.companyId));
      }
      if (Number(safeGet(this.vacancy, ['data', 'id'])) !== this.id) {
        actions.push(this.fetchVacancy(this.id));
      }
      if (actions.length) {
        await Promise.all(actions);
      }
      this.$emit('ready', { vacancy: this.vacancy.data });
    },
    async fetchCompany(id) {
      await this.$store.dispatch('company/fetchCompany', id);
    },
    async fetchVacancy(id) {
      await this.$store.dispatch('vacancy/fetchVacancy', id);
    },
  },
};
</script>
